import { FireLifecycleCard } from '../../components/FireLifecycle';
import { lifecycleTitle } from '@perdiem/trpc/fire-lifecycle';
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  Pressable,
  Alert,
  ActivityIndicator,
  Modal,
  Platform,
} from "react-native";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useIsFocused, usePathname, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import * as SecureStore from "expo-secure-store";
import { trpc } from "../../lib/trpc";
import { shuffledFireLoadingMessages } from "../../lib/fire-loading-messages";
import { formatFiAgeSummary } from "../../lib/fire-summary";
import { useNativeFireCalculation } from "../../hooks/useNativeFireCalculation";
import {
  trackFireCalculationCompleted,
  trackFireCalculationFailed,
  trackFireCalculationRequested,
  trackFireCalculationStatusChanged,
} from "../../lib/product-events";
import {
  Card,
  SectionHeader,
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Button,
  PageContainer,
  PageHeader,
  WebHead,
  LifeEventForm,
  LifeEventList,
  PlanHealthBanner,
  Money,
} from "../../components";
import { CurrencyInput } from "../../components/CurrencyInput";
import { CashFlowModal } from "../../components/CashFlowModal";
import { mapFirePlanToCashFlowData } from "../../components/cashflow/mappers";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useColors } from "../../lib/colors";
import {
  formatCurrency,
  formatDate,
  formatPercent,
  formatRelativeTime,
} from "../../lib/utils";
import {
  parseYear,
  toCreateLifeEventMutationInput,
  toUpdateLifeEventMutationInput,
  type GoalEvent,
  type LifeEventFormValues,
} from "../../lib/life-events";
import { useScreenReadyTelemetry } from "../../hooks/useScreenReadyTelemetry";

type Panel = "cashflow" | "events" | "montecarlo" | "drawdown" | "history";

const CALC_STATUS_LABELS: Record<string, string> = {
  starting: "Loading data…",
  precalculating: "Preparing projections…",
  simulating: "Running simulations…",
  saving: "Saving results…",
  done: "Done!",
};

const SCENARIOS_FEATURE_SEEN_KEY = "perdiem.fire-plan.scenarios-feature-seen.v1";

function getScenariosFeatureWebStorage(): Storage | null {
  if (Platform.OS !== "web" || typeof localStorage === "undefined") {
    return null;
  }

  if (
    typeof localStorage.getItem !== "function" ||
    typeof localStorage.setItem !== "function"
  ) {
    return null;
  }

  return localStorage;
}

async function readScenariosFeatureSeen(): Promise<boolean> {
  try {
    const storage = getScenariosFeatureWebStorage();
    if (storage) {
      return storage.getItem(SCENARIOS_FEATURE_SEEN_KEY) === "1";
    }

    return (await SecureStore.getItemAsync(SCENARIOS_FEATURE_SEEN_KEY)) === "1";
  } catch {
    return false;
  }
}

function persistScenariosFeatureSeen(): void {
  try {
    const storage = getScenariosFeatureWebStorage();
    if (storage) {
      storage.setItem(SCENARIOS_FEATURE_SEEN_KEY, "1");
      return;
    }

    SecureStore.setItemAsync(SCENARIOS_FEATURE_SEEN_KEY, "1").catch(() => {});
  } catch {
    // Ignore best-effort feature hint persistence failures.
  }
}

function getCalcErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Calculation failed. Please try again.";
}

function getMutationErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Panel Tab selector */
function PanelTabs({
  active,
  onChange,
}: {
  active: Panel;
  onChange: (p: Panel) => void;
}) {
  const tabs: { key: Panel; label: string }[] = [
    { key: "cashflow", label: "Cash Flow" },
    { key: "events", label: "Life Events" },
    { key: "montecarlo", label: "Projections" },
    { key: "drawdown", label: "Drawdown" },
    { key: "history", label: "History" },
  ];
  return (
    <View className="flex-row bg-gray-100 dark:bg-gray-700 rounded-xl p-1 mb-4">
      {tabs.map((t) => (
        <Pressable
          key={t.key}
          onPress={() => onChange(t.key)}
          className={`flex-1 py-2 rounded-lg items-center ${
            active === t.key ? "bg-white dark:bg-gray-800" : ""
          }`}
          style={active === t.key && Platform.OS !== 'web' ? { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 } : undefined}
        >
          <Text
            className={`text-sm font-medium ${
              active === t.key ? "text-gray-800 dark:text-gray-200" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {t.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

/** Panel 1: Year-by-year cash flow table */
function CashFlowPanel({
  entries,
  plan,
  birthYear,
  onRowPress,
}: {
  entries: LedgerEntry[];
  plan: FirePlan;
  birthYear: number | null;
  onRowPress: (year: number) => void;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          Run the FIRE calculation to see year-by-year projections.
        </Text>
      </Card>
    );
  }

  const fiYear = plan.beginYear;
  const currentYear = new Date().getFullYear();

  return (
    <Card>
      <SectionHeader title="Year-by-Year Cash Flow" />
      {/* Column headers */}
      <View className="flex-row items-center border-b border-gray-200 dark:border-gray-600 pb-2 mb-1 gap-1">
        <Text className="w-10 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3">Year</Text>
        {birthYear && (
          <Text className="w-8 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3">Age</Text>
        )}
        <Text className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 text-right">
          Income
        </Text>
        <Text className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 text-right">
          Spend
        </Text>
        <Text className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 text-right">
          Liquid NW
        </Text>
        <Text className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 text-right">
          Total NW
        </Text>
        <Text className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-3 dark:text-ink-dark-3 text-right">
          Success
        </Text>
      </View>
      {entries.map((entry) => {
        const isFiYear = entry.year === fiYear;
        const isPast = entry.year < currentYear;
        const successRate =
          entry.successRate != null ? Number(entry.successRate) : null;
        return (
          <Pressable
            key={entry.year}
            onPress={() => onRowPress(entry.year)}
            className={`flex-row items-center py-2 border-b border-gray-50 dark:border-gray-700 gap-1 ${
              isFiYear ? "bg-forest-50 dark:bg-forest-700/20 -mx-4 px-4" : ""
            } ${isPast ? "opacity-50" : ""} active:bg-gray-50 dark:active:bg-gray-700/40`}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as object) : undefined}
          >
            <Money weight={500} className="w-10 text-xs text-gray-800 dark:text-gray-200">
              {String(entry.year)}
            </Money>
            {birthYear && (
              <View className="w-8">
                <Money
                  weight={500}
                  className={`text-xs ${isFiYear ? "text-forest-600 dark:text-green-500" : "text-gray-800 dark:text-gray-200"}`}
                >
                  {String(entry.year - birthYear)}
                </Money>
                {isFiYear && (
                  <Text className="text-xs text-forest-600 dark:text-green-500 font-bold">FI!</Text>
                )}
              </View>
            )}
            {!birthYear && isFiYear && (
              <Text className="text-xs text-forest-600 dark:text-green-500 font-bold">FI!</Text>
            )}
            <Money className="flex-1 text-xs text-forest-600 dark:text-green-500 text-right">
              {formatCurrency(Number(entry.income ?? 0), { compact: true })}
            </Money>
            <Money className="flex-1 text-xs text-gray-700 dark:text-gray-300 text-right">
              {formatCurrency(Math.abs(Number(entry.expenses ?? 0)), {
                compact: true,
              })}
            </Money>
            <Money weight={500} className="flex-1 text-xs text-gray-800 dark:text-gray-200 text-right">
              {formatCurrency(Number(entry.liquidNetWorth ?? 0), {
                compact: true,
              })}
            </Money>
            <Money weight={500} className="flex-1 text-xs text-gray-800 dark:text-gray-200 text-right">
              {formatCurrency(Number(entry.netWorth ?? 0), { compact: true })}
            </Money>
            <View className="flex-1 items-end">
              <Money
                weight={500}
                className={`text-xs ${
                  successRate == null
                    ? "text-gray-400 dark:text-gray-500"
                    : successRate >= 0.8
                    ? "text-forest-600 dark:text-green-500"
                    : successRate >= 0.5
                      ? "text-amber-600"
                      : "text-red-600 dark:text-red-400"
                }`}
              >
                {successRate == null ? "—" : formatPercent(successRate, 0)}
              </Money>
            </View>
          </Pressable>
        );
      })}
    </Card>
  );
}

/** Panel 2: Monte Carlo fan chart (simplified percentile bands as bar chart) */
function MonteCarloPanel({
  entries,
  plan,
  birthYear,
}: {
  entries: LedgerEntry[];
  plan: FirePlan;
  birthYear: number | null;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          Run the FIRE calculation to see Monte Carlo projections.
        </Text>
      </Card>
    );
  }

  const fiYear = plan.beginYear;
  const threshold = Number(plan.threshold ?? 0.8);

  // Show every 5 years to keep the chart manageable
  const sampleEntries = entries.filter((_, i) => i % 5 === 0);
  const maxNetWorth = Math.max(...entries.map((e) => Number(e.netWorth ?? 0)));

  return (
    <Card>
      <SectionHeader title="Portfolio Projections" />

      {/* Legend */}
      <View className="flex-row flex-wrap gap-3 mb-4">
        {[
          { label: "90th %ile", color: "bg-green-200" },
          { label: "75th %ile", color: "bg-green-400" },
          { label: "Median", color: "bg-forest-600" },
          { label: "25th %ile", color: "bg-orange-400" },
          { label: "10th %ile", color: "bg-red-400" },
        ].map((item) => (
          <View key={item.label} className="flex-row items-center gap-1">
            <View className={`w-3 h-3 rounded-full ${item.color}`} />
            <Text className="text-xs text-gray-500 dark:text-gray-400">{item.label}</Text>
          </View>
        ))}
      </View>

      {/* Simplified bar chart showing net worth distribution by sampled year */}
      {sampleEntries.map((entry) => {
        const isFiYear = entry.year === fiYear;
        const successRate =
          entry.successRate != null ? Number(entry.successRate) : null;
        const p50 = Number(entry.netWorth ?? 0);
        const p90 = p50 * 1.4;
        const p75 = p50 * 1.2;
        const p25 = p50 * 0.8;
        const p10 = p50 * 0.6;

        return (
          <View key={entry.year} className="mb-3">
            <View className="flex-row justify-between mb-1">
              <Text
                className={`text-xs font-semibold ${isFiYear ? "text-green-600 dark:text-green-500" : "text-gray-600 dark:text-gray-400"}`}
              >
                {entry.year}
                {isFiYear
                  ? birthYear
                    ? ` (FI age ${entry.year - birthYear})`
                    : " (FI)"
                  : ""}
              </Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {formatCurrency(p50, { compact: true })} median
              </Text>
            </View>
            {/* Stacked percentile bars */}
            <View className="relative h-5 bg-red-100 rounded-full overflow-hidden">
              <View
                className="absolute left-0 top-0 bottom-0 bg-red-300 rounded-full"
                style={{
                  width: `${Math.min(100, (p10 / maxNetWorth) * 100)}%`,
                }}
              />
              <View
                className="absolute left-0 top-0 bottom-0 bg-orange-300 rounded-full"
                style={{
                  width: `${Math.min(100, (p25 / maxNetWorth) * 100)}%`,
                }}
              />
              <View
                className="absolute left-0 top-0 bottom-0 bg-green-500 rounded-full"
                style={{
                  width: `${Math.min(100, (p50 / maxNetWorth) * 100)}%`,
                }}
              />
              <View
                className="absolute left-0 top-0 bottom-0 bg-green-300 rounded-full opacity-60"
                style={{
                  width: `${Math.min(100, (p75 / maxNetWorth) * 100)}%`,
                }}
              />
              <View
                className="absolute left-0 top-0 bottom-0 bg-green-200 rounded-full opacity-40"
                style={{
                  width: `${Math.min(100, (p90 / maxNetWorth) * 100)}%`,
                }}
              />
            </View>
            {/* Success rate */}
            <Text className="text-xs text-gray-400 mt-0.5 text-right">
              {successRate == null
                ? "Success not modeled for this year"
                : `${formatPercent(successRate, 0)} success`}
            </Text>
          </View>
        );
      })}

      <View className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <Text className="text-xs text-gray-400">
          Threshold: {formatPercent(threshold, 0)} success rate to declare FI
        </Text>
      </View>
    </Card>
  );
}

/** Panel 3: Account drawdown waterfall */
function DrawdownPanel({ entries }: { entries: LedgerEntry[] }) {
  if (entries.length === 0) {
    return (
      <Card>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          Run the FIRE calculation to see the drawdown schedule.
        </Text>
      </Card>
    );
  }

  // Sample every 5 years
  const sampleEntries = entries.filter((_, i) => i % 5 === 0);
  const withdrawalBuckets = sampleEntries.map((entry) => {
    const cashFlow = Number(entry.cash ?? 0);
    const taxableFlow = Number(entry.taxable ?? 0);
    const taxDeferredFlow = Number(entry.taxDeferred ?? 0);
    const taxExemptFlow = Number(entry.taxExempt ?? 0);

    // Ledger bucket fields are annual net flows, not balances. Positive values
    // represent outflows from a bucket to fund the year, while negative values
    // represent contributions or internal transfers. A common internal transfer
    // is a Roth conversion, which appears as +taxDeferred and -taxExempt in the
    // same year. Exclude that from the user-facing drawdown chart so we show
    // spending withdrawals rather than tax-strategy plumbing.
    const rothConversionProxy = Math.min(
      Math.max(0, taxDeferredFlow),
      Math.max(0, -taxExemptFlow),
    );

    const cash = Math.max(0, cashFlow);
    const taxable = Math.max(0, taxableFlow);
    const deferred = Math.max(0, taxDeferredFlow - rothConversionProxy);
    const exempt = Math.max(0, taxExemptFlow);
    const total = cash + taxable + deferred + exempt;

    return {
      year: entry.year,
      cash,
      taxable,
      deferred,
      exempt,
      total,
    };
  });
  const maxNetWorth = Math.max(
    1,
    ...withdrawalBuckets.map((entry) => entry.total),
  );

  return (
    <Card>
      <SectionHeader title="Account Withdrawal Schedule" />
      <Text className="mb-4 text-xs text-gray-400">
        Shows estimated annual withdrawals used to fund spending, excluding internal Roth conversion moves.
      </Text>

      {/* Legend */}
      <View className="flex-row flex-wrap gap-3 mb-4">
        {[
          { label: "Cash", color: "bg-blue-400" },
          { label: "Taxable", color: "bg-green-400" },
          { label: "Tax-Deferred", color: "bg-orange-400" },
          { label: "Tax-Exempt", color: "bg-purple-400" },
        ].map((item) => (
          <View key={item.label} className="flex-row items-center gap-1">
            <View className={`w-3 h-3 rounded-full ${item.color}`} />
            <Text className="text-xs text-gray-500 dark:text-gray-400">{item.label}</Text>
          </View>
        ))}
      </View>

      {withdrawalBuckets.map((entry) => {
        const total = entry.total;
        const scale = maxNetWorth > 0 ? total / maxNetWorth : 0;

        const cashPct = total > 0 ? entry.cash / total : 0;
        const taxablePct = total > 0 ? entry.taxable / total : 0;
        const deferredPct = total > 0 ? entry.deferred / total : 0;
        const exemptPct = total > 0 ? entry.exempt / total : 0;

        return (
          <View key={entry.year} className="mb-3">
            <View className="flex-row justify-between mb-1">
              <Text className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                {entry.year}
              </Text>
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {formatCurrency(total, { compact: true })}
              </Text>
            </View>
            {/* Stacked bar */}
            <View
              className="h-5 flex-row rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700"
              style={{ width: `${scale * 100}%` }}
            >
              {cashPct > 0 && (
                <View
                  className="bg-blue-400 h-full"
                  style={{ flex: cashPct }}
                />
              )}
              {taxablePct > 0 && (
                <View
                  className="bg-green-400 h-full"
                  style={{ flex: taxablePct }}
                />
              )}
              {deferredPct > 0 && (
                <View
                  className="bg-orange-400 h-full"
                  style={{ flex: deferredPct }}
                />
              )}
              {exemptPct > 0 && (
                <View
                  className="bg-purple-400 h-full"
                  style={{ flex: exemptPct }}
                />
              )}
            </View>
          </View>
        );
      })}
    </Card>
  );
}

type PersonProfile = {
  id: string;
  type: "primary" | "spouse";
  isSelf?: boolean;
  firstName: string | null;
  lastName: string | null;
  birthYear: number | null;
  deathYear: number | null;
  targetFireYear: number | null;
};

type IncomeStreamSummary = {
  id: string;
  name: string;
  type: string;
  annualAmount: number;
  growthRate: number;
  begin: string | null;
  end: string | null;
  personId: string | null;
};

type LedgerEntry = {
  year: number;
  income: string | null;
  expenses: string | null;
  liquidNetWorth: string | null;
  netWorth: string | null;
  wages: string | null;
  passiveIncome: string | null;
  socialSecurityIncome: string | null;
  cash: string | null;
  taxable: string | null;
  taxDeferred: string | null;
  taxExempt: string | null;
  taxPaid: string | null;
  successRate: string | null;
};

type FirePlan = {
  id: string;
  beginYear: number;
  threshold: number;
  ledgerEntries: LedgerEntry[];
};

type LastRecalcImpact = {
  completedAt: string;
  triggeredAt: string;
  reasons: Array<{
    source: string;
    action: string;
    entityId: string;
    changedFields: string[];
    at: string;
    entityName?: string | null;
    description?: string | null;
    changes?: Array<{
      field: string;
      before?: unknown;
      after?: unknown;
    }>;
  }>;
  changeSummary: string | null;
  summary: string;
  fiYearBefore: number | null;
  fiYearAfter: number | null;
  successBefore: number | null;
  successAfter: number | null;
  netWorthBefore: number | null;
  netWorthAfter: number | null;
};

const RECALC_TRIGGER_LABELS: Record<string, Record<string, string>> = {
  accounts: {
    insert: "a new account was connected",
    update: "an account balance changed",
    delete: "an account was removed",
  },
  income_streams: {
    insert: "an income source was added",
    update: "an income source was updated",
    delete: "an income source was removed",
  },
  budget_entries: {
    insert: "a budget entry was added",
    update: "a budget entry was updated",
    delete: "a budget entry was removed",
  },
  goals: {
    insert: "a life event was added",
    update: "a life event was updated",
    delete: "a life event was removed",
  },
  persons: {
    insert: "a profile was created",
    update: "retirement settings were changed",
    delete: "a profile was removed",
  },
  fire_plans: {
    insert: "a FIRE plan was created",
    update: "FIRE targets were changed",
    delete: "a FIRE plan was removed",
  },
  tax_strategy: {
    insert: "a tax strategy was created",
    update: "tax strategy settings were updated",
    delete: "a tax strategy was removed",
  },
  liabilities: {
    insert: "a liability was added",
    update: "liability planning was updated",
    delete: "a liability was removed",
  },
};

function joinWithAnd(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function getRecalcTriggerText(reasons: LastRecalcImpact["reasons"]): string {
  const seen = new Set<string>();
  const triggers: string[] = [];

  for (const reason of reasons ?? []) {
    const key = `${reason.source}:${reason.action}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const label = RECALC_TRIGGER_LABELS[reason.source]?.[reason.action];
    if (label) triggers.push(label);
  }

  return triggers.length > 0 ? joinWithAnd(triggers) : "plan inputs changed";
}

function formatRecalcImpact(item: LastRecalcImpact, birthYear: number | null): string {
  const formatFiLabel = (year: number) =>
    birthYear ? `FI age ${year - birthYear}` : `FI year ${year}`;

  if (item.fiYearBefore == null && item.fiYearAfter == null) {
    return "FI is still not reachable";
  }
  if (item.fiYearBefore == null && item.fiYearAfter != null) {
    return `FI changed from never to ${formatFiLabel(item.fiYearAfter)}`;
  }
  if (item.fiYearBefore != null && item.fiYearAfter == null) {
    return `FI was ${formatFiLabel(item.fiYearBefore)} but is no longer reachable`;
  }
  if (item.fiYearBefore === item.fiYearAfter) {
    return `${formatFiLabel(item.fiYearAfter!)} held steady`;
  }
  return `${formatFiLabel(item.fiYearBefore!)} moved to ${formatFiLabel(item.fiYearAfter!)}`;
}

function buildLastImpactSentence(item: LastRecalcImpact, birthYear: number | null): string {
  const triggerText = getRecalcTriggerText(item.reasons);
  const triggerSentence = triggerText.charAt(0).toUpperCase() + triggerText.slice(1);
  const when = formatRelativeTime(item.completedAt);
  const impact = formatRecalcImpact(item, birthYear);
  return `${triggerSentence} ${when}. ${impact}.`;
}

function formatMetricShift(
  label: string,
  before: string | null,
  after: string | null,
): string | null {
  if (before == null && after == null) return null;
  if (before == null && after != null) return `${label}: now ${after}`;
  if (before != null && after == null) return `${label}: no longer available`;
  if (before === after) return `${label}: held at ${after}`;
  return `${label}: ${before} -> ${after}`;
}

function HistoryPanel({
  history,
  birthYear,
  isLoading,
}: {
  history: LastRecalcImpact[];
  birthYear: number | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (history.length === 0) {
    return (
      <Card>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          Automatic FIRE recalculations will appear here once plan inputs change.
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeader title="Plan Change History" />
      <View className="gap-3">
        {history.map((item, index) => {
          const fiShift = formatMetricShift(
            birthYear ? "FI age" : "FI year",
            item.fiYearBefore != null
              ? birthYear
                ? String(item.fiYearBefore - birthYear)
                : String(item.fiYearBefore)
              : null,
            item.fiYearAfter != null
              ? birthYear
                ? String(item.fiYearAfter - birthYear)
                : String(item.fiYearAfter)
              : null,
          );
          const successShift = formatMetricShift(
            "Success",
            item.successBefore != null ? formatPercent(item.successBefore, 0) : null,
            item.successAfter != null ? formatPercent(item.successAfter, 0) : null,
          );
          const netWorthShift = formatMetricShift(
            "Net worth",
            item.netWorthBefore != null
              ? formatCurrency(item.netWorthBefore, { compact: true, decimals: 0 })
              : null,
            item.netWorthAfter != null
              ? formatCurrency(item.netWorthAfter, { compact: true, decimals: 0 })
              : null,
          );
          const detailPills = [fiShift, successShift, netWorthShift].filter(
            (value): value is string => Boolean(value),
          );

          return (
            <View
              key={`${item.completedAt}-${index}`}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 p-4"
            >
              <View className="flex-row items-start justify-between gap-3">
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatDate(item.completedAt, "long")}
                  </Text>
                  <Text className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Updated {formatRelativeTime(item.completedAt)}
                  </Text>
                </View>
                <Text className="text-xs text-gray-500 dark:text-gray-400">
                  Triggered {formatRelativeTime(item.triggeredAt)}
                </Text>
              </View>

              <Text className="mt-3 text-sm text-gray-800 dark:text-gray-200">
                {item.summary}
              </Text>
              <Text className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {item.changeSummary ?? `Triggered by ${getRecalcTriggerText(item.reasons)}.`}
              </Text>

              {detailPills.length > 0 && (
                <View className="mt-3 flex-row flex-wrap gap-2">
                  {detailPills.map((detail) => (
                    <View
                      key={detail}
                      className="rounded-full bg-white dark:bg-gray-900 px-3 py-1.5 border border-gray-200 dark:border-gray-700"
                    >
                      <Text className="text-xs text-gray-600 dark:text-gray-300">
                        {detail}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </Card>
  );
}

// ─── What-If Scenario Modal ───────────────────────────────────────────────

type ScenarioResult = {
  beginYear: number | null;
  successRate: number | null;
};

function WhatIfModal({
  visible,
  onClose,
  currentFiYear,
  currentSuccessRate,
  defaultMonthlyExpenses,
  defaultMonthlyIncome,
  birthYear,
}: {
  visible: boolean;
  onClose: () => void;
  currentFiYear: number | null | undefined;
  currentSuccessRate: number | null;
  defaultMonthlyExpenses: number;
  defaultMonthlyIncome: number;
  birthYear: number | null;
}) {
  const [monthlyExpenses, setMonthlyExpenses] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [result, setResult] = useState<ScenarioResult | null>(null);

  const scenarioMutation = trpc.fire.runScenario.useMutation({
    onSuccess: (data) => {
      setResult({
        beginYear: data.beginYear,
        successRate: data.successRate,
      });
    },
    onError: (error) => {
      Alert.alert("Error", error.message || "Failed to run scenario. Please try again.");
    },
  });

  // Pre-fill defaults when modal opens
  const handleOpen = useCallback(() => {
    setMonthlyExpenses(
      defaultMonthlyExpenses > 0
        ? String(Math.round(defaultMonthlyExpenses))
        : "",
    );
    setMonthlyIncome(
      defaultMonthlyIncome > 0 ? String(Math.round(defaultMonthlyIncome)) : "",
    );
    setResult(null);
    scenarioMutation.reset();
  }, [defaultMonthlyExpenses, defaultMonthlyIncome, scenarioMutation]);

  // Reset on open
  useEffect(() => {
    if (visible) handleOpen();
  }, [visible, handleOpen]);

  const handleRunScenario = () => {
    const expenses = parseFloat(monthlyExpenses);
    const income = parseFloat(monthlyIncome);
    const expenseChanged =
      Number.isFinite(expenses) &&
      Math.abs(expenses - Math.round(defaultMonthlyExpenses)) >= 1;
    const incomeChanged =
      Number.isFinite(income) &&
      Math.abs(income - Math.round(defaultMonthlyIncome)) >= 1;

    if (isNaN(expenses) && isNaN(income)) {
      Alert.alert(
        "Missing Input",
        "Enter at least one value to run a scenario.",
      );
      return;
    }
    if (!expenseChanged && !incomeChanged) {
      Alert.alert(
        "No changes",
        "Change at least one scenario input before running the calculation.",
      );
      return;
    }

    scenarioMutation.mutate({
      ...(expenseChanged ? { modifiedExpenses: expenses * 12 } : {}),
      ...(incomeChanged ? { modifiedIncome: income * 12 } : {}),
    });
  };

  const handleReset = () => {
    setResult(null);
    scenarioMutation.reset();
    onClose();
  };

  const yearDiff =
    result?.beginYear != null && currentFiYear != null
      ? result.beginYear - currentFiYear
      : null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View className="flex-1 bg-canvas dark:bg-canvas-dark">
        {/* Header */}
        <View className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-4 pt-14 pb-4">
          <View className="flex-row items-center justify-between">
            <Text className="text-xl font-bold text-gray-800 dark:text-gray-200">What If?</Text>
            <Pressable onPress={handleReset} hitSlop={12}>
              <Text className="text-sm font-semibold text-gray-500 dark:text-gray-400">Close</Text>
            </Pressable>
          </View>
          <Text className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Explore how changes to your income or spending affect your FI age.
          </Text>
        </View>

        <ScrollView
          className="flex-1 px-4 py-6"
          keyboardShouldPersistTaps="handled"
        >
          {/* Inputs */}
          <Card className="mb-4">
            <SectionHeader title="Scenario Inputs" />
            <Text className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Adjust your monthly values below. Leave a field empty to keep the
              current value.
            </Text>

            <CurrencyInput
              label="Monthly Expenses"
              value={monthlyExpenses}
              onChangeText={setMonthlyExpenses}
              placeholder={
                defaultMonthlyExpenses > 0
                  ? String(Math.round(defaultMonthlyExpenses))
                  : "0"
              }
              size="sm"
              containerClassName="mb-3"
            />

            <CurrencyInput
              label="Monthly Income"
              value={monthlyIncome}
              onChangeText={setMonthlyIncome}
              placeholder={
                defaultMonthlyIncome > 0
                  ? String(Math.round(defaultMonthlyIncome))
                  : "0"
              }
              size="sm"
              containerClassName="mb-4"
            />

            <Button
              onPress={handleRunScenario}
              loading={scenarioMutation.isPending}
            >
              Run Scenario
            </Button>
          </Card>

          {/* Results */}
          {result && (
            <Card className="mb-4 border border-green-100 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
              <SectionHeader title="Scenario Result" />

              {result.beginYear != null ? (
                <>
                  <Text className="text-2xl font-bold text-green-800 dark:text-green-300 mb-1">
                    {birthYear
                      ? `FI at Age ${result.beginYear - birthYear}`
                      : `FI Year: ${result.beginYear}`}
                  </Text>
                  {birthYear && (
                    <Text className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                      Year {result.beginYear}
                    </Text>
                  )}
                  {yearDiff !== null && (
                    <Text className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      {yearDiff === 0
                        ? "Same as your current plan"
                        : yearDiff < 0
                          ? `${Math.abs(yearDiff)} year${Math.abs(yearDiff) !== 1 ? "s" : ""} earlier than current plan`
                          : `${yearDiff} year${yearDiff !== 1 ? "s" : ""} later than current plan`}
                    </Text>
                  )}
                  {currentFiYear == null && (
                    <Text className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                      Your current plan has no FI age yet — this scenario
                      reaches it!
                    </Text>
                  )}
                </>
              ) : (
                <Text className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-2">
                  No FI age reached with these inputs
                </Text>
              )}

              {result.successRate != null && (
                <View className="flex-row items-center gap-2 mt-1">
                  <Text className="text-sm text-gray-600 dark:text-gray-400">Success rate:</Text>
                  {currentSuccessRate != null ? (
                    <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {formatPercent(currentSuccessRate, 0)} {"->"}{" "}
                      {formatPercent(result.successRate, 0)}
                    </Text>
                  ) : (
                    <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {formatPercent(result.successRate, 0)}
                    </Text>
                  )}
                </View>
              )}
            </Card>
          )}

          {/* Current plan reference */}
          <Card>
            <SectionHeader title="Current Plan" />
            <View className="flex-row justify-between">
              <Text className="text-sm text-gray-600 dark:text-gray-400">FI Age</Text>
              <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {currentFiYear && birthYear
                  ? `${currentFiYear - birthYear} (${currentFiYear})`
                  : (currentFiYear ?? "Not reached")}
              </Text>
            </View>
            {currentSuccessRate != null && (
              <View className="flex-row justify-between mt-1">
                <Text className="text-sm text-gray-600 dark:text-gray-400">Success Rate</Text>
                <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {formatPercent(currentSuccessRate, 0)}
                </Text>
              </View>
            )}
            <View className="flex-row justify-between mt-1">
              <Text className="text-sm text-gray-600 dark:text-gray-400">Monthly Expenses</Text>
              <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {defaultMonthlyExpenses > 0
                  ? formatCurrency(defaultMonthlyExpenses, { decimals: 0 })
                  : "—"}
              </Text>
            </View>
            <View className="flex-row justify-between mt-1">
              <Text className="text-sm text-gray-600 dark:text-gray-400">Monthly Income</Text>
              <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {defaultMonthlyIncome > 0
                  ? formatCurrency(defaultMonthlyIncome, { decimals: 0 })
                  : "—"}
              </Text>
            </View>
          </Card>
        </ScrollView>
      </View>
    </Modal>
  );
}

export default function FirePlanScreen() {
  const isFocused = useIsFocused();
  const router = useRouter();
  const pathname = usePathname();
  const c = useColors();
  const isDesktop = useIsDesktop();
  const [panel, setPanel] = useState<Panel>("cashflow");
  const [refreshing, setRefreshing] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState(0);
  const [calcStatus, setCalcStatus] = useState("");
  const [calcMessages, setCalcMessages] = useState(() =>
    shuffledFireLoadingMessages(),
  );
  const [calcMessageIndex, setCalcMessageIndex] = useState(0);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [showScenariosFeatureHint, setShowScenariosFeatureHint] = useState(false);
  const [isScenariosCtaHovered, setIsScenariosCtaHovered] = useState(false);
  const webCalculationStartedAtRef = useRef<number | null>(null);
  const webCalculationTerminalReportedRef = useRef(false);
  const lastWebCalculationStatusRef = useRef<string | null>(null);

  const utils = trpc.useUtils();
  const planQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const lastImpactQ = trpc.fire.getLastRecalcImpact.useQuery(undefined, { enabled: isFocused });
  const recalcHistoryQ = trpc.fire.listRecalcHistory.useQuery(undefined, { enabled: isFocused });
  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: isFocused });
  const incomeQ = trpc.income.list.useQuery(undefined, { enabled: isFocused });
  const budgetEntriesQ = trpc.budget.getEntries.useQuery(undefined, { enabled: isFocused });
  const cashflowQ = trpc.budget.annualizedCashflow.useQuery(undefined, { enabled: isFocused });
  useScreenReadyTelemetry({
    ready: !planQ.isLoading,
    screenName: 'fire-plan',
  });
  const calculateMutation = trpc.fire.calculate.useMutation();
  const fireCalculationEventMetadata = {
    hasExistingPlan: planQ.data != null,
    trigger: planQ.data != null ? "recalculate" : "initial_calculate",
  } as const;
  const nativeCalculation = useNativeFireCalculation({
    queuedLabel: CALC_STATUS_LABELS.precalculating,
    runningLabel: CALC_STATUS_LABELS.simulating,
    doneLabel: CALC_STATUS_LABELS.done,
    screen: "fire-plan",
    source: "fire_plan",
    metadata: fireCalculationEventMetadata,
  });
  const {
    error: nativeCalcError,
    isCalculating: isNativeCalculating,
    progress: nativeCalcProgress,
    reset: resetNativeCalculation,
    start: startNativeCalculation,
    statusLabel: nativeCalcStatus,
  } = nativeCalculation;

  trpc.fire.calculationProgress.useSubscription(undefined, {
    enabled: Platform.OS === "web" && isCalculating,
    onData(event) {
      if (
        event.status !== "done" &&
        event.status !== "error" &&
        lastWebCalculationStatusRef.current !== event.status
      ) {
        lastWebCalculationStatusRef.current = event.status;
        trackFireCalculationStatusChanged({
          route: pathname,
          screen: "fire-plan",
          source: "fire_plan",
          transport: "subscription",
          status: event.status,
          progress: event.progress,
          metadata: fireCalculationEventMetadata,
        });
      }

      setCalcProgress(event.progress);
      setCalcStatus(CALC_STATUS_LABELS[event.status] ?? event.status);
      if (event.status === "done" || event.status === "error") {
        setIsCalculating(false);
        if (event.status === "done") {
          if (!webCalculationTerminalReportedRef.current) {
            webCalculationTerminalReportedRef.current = true;
            trackFireCalculationCompleted({
              route: pathname,
              screen: "fire-plan",
              source: "fire_plan",
              transport: "subscription",
              durationMs:
                webCalculationStartedAtRef.current != null
                  ? Date.now() - webCalculationStartedAtRef.current
                  : null,
              fiYear: event.fiYear ?? null,
              fiAge: event.fiAge ?? null,
              metadata: fireCalculationEventMetadata,
            });
          }
          utils.fire.getPlan.invalidate();
          utils.fire.getLastRecalcImpact.invalidate();
        }
        if (event.status === "error") {
          if (!webCalculationTerminalReportedRef.current) {
            webCalculationTerminalReportedRef.current = true;
            trackFireCalculationFailed({
              route: pathname,
              screen: "fire-plan",
              source: "fire_plan",
              transport: "subscription",
              durationMs:
                webCalculationStartedAtRef.current != null
                  ? Date.now() - webCalculationStartedAtRef.current
                  : null,
              errorMessage: event.error ?? "Calculation failed.",
              metadata: fireCalculationEventMetadata,
            });
          }
          Alert.alert("Error", event.error ?? "Calculation failed.");
        }
      }
    },
    onError: (err) => {
      setIsCalculating(false);
      if (!webCalculationTerminalReportedRef.current) {
        webCalculationTerminalReportedRef.current = true;
        trackFireCalculationFailed({
          route: pathname,
          screen: "fire-plan",
          source: "fire_plan",
          transport: "subscription",
          durationMs:
            webCalculationStartedAtRef.current != null
              ? Date.now() - webCalculationStartedAtRef.current
              : null,
          errorMessage: err.message,
          metadata: fireCalculationEventMetadata,
        });
      }
      Alert.alert("Error", "Calculation failed. Please try again.");
    },
  });

  useEffect(() => {
    const calculationActive =
      Platform.OS === "web" ? isCalculating : isNativeCalculating;
    if (!calculationActive) return;

    const intervalId = setInterval(() => {
      setCalcMessageIndex(
        (currentIndex) => (currentIndex + 1) % calcMessages.length,
      );
    }, 4800);

    return () => clearInterval(intervalId);
  }, [calcMessages, isCalculating, isNativeCalculating]);

  useEffect(() => {
    if (Platform.OS === "web" || !nativeCalcError) return;
    Alert.alert("Error", getCalcErrorMessage(new Error(nativeCalcError)));
  }, [nativeCalcError]);

  useEffect(() => {
    let cancelled = false;

    readScenariosFeatureSeen().then((seen) => {
      if (!cancelled) {
        setShowScenariosFeatureHint(!seen);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      planQ.refetch(),
      profilesQ.refetch(),
      lastImpactQ.refetch(),
      recalcHistoryQ.refetch(),
      accountsQ.refetch(),
      incomeQ.refetch(),
      budgetEntriesQ.refetch(),
      cashflowQ.refetch(),
    ]);
    setRefreshing(false);
  }, [
    planQ,
    profilesQ,
    lastImpactQ,
    recalcHistoryQ,
    accountsQ,
    incomeQ,
    budgetEntriesQ,
    cashflowQ,
  ]);

  const handleCalculate = useCallback(async () => {
    if (calculateMutation.isPending || isCalculating || isNativeCalculating)
      return;

    setCalcMessages(shuffledFireLoadingMessages());
    setCalcMessageIndex(0);
    setCalcProgress(0);
    setCalcStatus(CALC_STATUS_LABELS.starting);
    setIsCalculating(true);

    if (Platform.OS === "web") {
      webCalculationStartedAtRef.current = Date.now();
      webCalculationTerminalReportedRef.current = false;
      lastWebCalculationStatusRef.current = null;
      trackFireCalculationRequested({
        route: pathname,
        screen: "fire-plan",
        source: "fire_plan",
        transport: "subscription",
        metadata: fireCalculationEventMetadata,
      });
      return;
    }

    setIsCalculating(false);
    resetNativeCalculation();
    void startNativeCalculation();
  }, [
    calculateMutation.isPending,
    isCalculating,
    isNativeCalculating,
    pathname,
    planQ.data,
    resetNativeCalculation,
    startNativeCalculation,
  ]);

  const lifecycleQ = trpc.fire.getLifecycle.useQuery(undefined, { refetchInterval: 30000 });
  const plan = planQ.data as FirePlan | null | undefined;
  const isDemo = householdQ.data?.isDemo ?? false;
  const calculationActive =
    Platform.OS === "web" ? isCalculating : isNativeCalculating;
  const displayedCalcProgress =
    Platform.OS === "web" ? calcProgress : nativeCalcProgress;
  const displayedCalcStatus =
    Platform.OS === "web" ? calcStatus : nativeCalcStatus;
  const entries = plan?.ledgerEntries ?? [];
  const fiYear = plan?.beginYear;
  const primaryProfile = (profilesQ.data ?? []).find(
    (p: PersonProfile) => p.type === "primary",
  ) as PersonProfile | undefined;
  const spouseProfile = (profilesQ.data ?? []).find(
    (p: PersonProfile) => p.type === "spouse",
  ) as PersonProfile | undefined;
  const currentProfile = (profilesQ.data ?? []).find(
    (p: PersonProfile) => p.isSelf,
  ) as PersonProfile | undefined;
  const birthYear = primaryProfile?.birthYear ?? null;
  const fiAge = birthYear && fiYear ? fiYear - birthYear : null;
  const currentBirthYear = currentProfile?.birthYear ?? null;
  const currentProfileName = currentProfile?.firstName ?? null;
  const spouseBirthYear = spouseProfile?.birthYear ?? null;
  const spouseFiAge =
    spouseBirthYear && fiYear ? fiYear - spouseBirthYear : null;
  const accounts = accountsQ.data ?? [];
  const incomeStreams = incomeQ.data ?? [];
  const budgetEntries = budgetEntriesQ.data ?? [];
  const missingInputs: string[] = [];

  if (accounts.length === 0) missingInputs.push("a connected account");
  if (budgetEntries.length === 0) missingInputs.push("a monthly budget");

  const hasSparseInputs = missingInputs.length > 0;
  const missingInputsCopy =
    missingInputs.length === 1
      ? missingInputs[0]
      : `${missingInputs.slice(0, -1).join(", ")}, and ${missingInputs.at(-1)}`;

  // Determine if any person is in fixed (custom) mode
  const profiles = (profilesQ.data ?? []) as PersonProfile[];
  const anyFixed = profiles.some((p) => p.targetFireYear !== null);

  // For fixed mode: find the success rate at the target year from ledger
  const targetYear = anyFixed
    ? Math.max(...profiles.map((p) => p.targetFireYear ?? 0))
    : null;
  const targetEntry = targetYear
    ? entries.find((e) => e.year === targetYear)
    : null;
  const targetSuccessRate = targetEntry
    ? Number(targetEntry.successRate ?? 0)
    : null;
  const lastImpact = (lastImpactQ.data ?? null) as LastRecalcImpact | null;
  const recalcHistory = (recalcHistoryQ.data ?? []) as LastRecalcImpact[];

  // What-If defaults: derive monthly income/expenses from annualized cashflow or ledger
  const cashflow = cashflowQ.data as
    | { annualIncome: number; annualExpenses: number }
    | null
    | undefined;
  const defaultMonthlyExpenses = cashflow?.annualExpenses
    ? cashflow.annualExpenses / 12
    : entries.length > 0
      ? Math.abs(Number(entries[0]?.expenses ?? 0)) / 12
      : 0;
  const defaultMonthlyIncome = cashflow?.annualIncome
    ? cashflow.annualIncome / 12
    : entries.length > 0
      ? Number(entries[0]?.income ?? 0) / 12
      : 0;

  // Current success rate from the FI year entry or last entry
  const fiEntry = fiYear ? entries.find((e) => e.year === fiYear) : null;
  const currentSuccessRate = fiEntry ? Number(fiEntry.successRate ?? 0) : null;

  const handleOpenScenarios = useCallback(() => {
    setShowScenariosFeatureHint(false);
    persistScenariosFeatureSeen();
    router.push('/(app)/scenarios');
  }, [router]);

  const retirementNoticesByYear = useMemo(() => {
    const wageTypes = new Set(["wages", "gig", "military"]);
    const notices = new Map<number, string[]>();
    const wageStreams = (incomeStreams as IncomeStreamSummary[]).filter((stream) =>
      wageTypes.has(stream.type),
    );

    for (const profile of profiles) {
      const hasWageStream = wageStreams.some(
        (stream) => stream.personId === profile.id,
      );
      if (!hasWageStream) continue;

      const retirementYear = lifecycleQ.data?.persons.find(p => p.id === profile.id)?.actualRetirementYear ?? profile.targetFireYear ?? fiYear ?? null;
      if (retirementYear == null) continue;

      const displayName =
        profile.firstName ?? (profile.type === "primary" ? "Primary" : "Spouse");
      const nextNotices = notices.get(retirementYear) ?? [];
      notices.set(retirementYear, [
        ...nextNotices,
        `${displayName} retires this year`,
      ]);
    }

    return notices;
  }, [fiYear, incomeStreams, profiles]);

  useEffect(() => {
    void utils.fire.getYearDetail.invalidate();
  }, [planQ.dataUpdatedAt, utils]);

  const yearDetailQ = trpc.fire.getYearDetail.useQuery(
    { year: selectedYear ?? 0 },
    { enabled: selectedYear !== null },
  );
  const yearDetailData = useMemo(() => {
    if (selectedYear == null || !yearDetailQ.data) return undefined;
    return mapFirePlanToCashFlowData(
      selectedYear,
      yearDetailQ.data,
      entries,
      currentBirthYear,
      currentProfileName,
      fiYear,
      retirementNoticesByYear.get(selectedYear),
      setSelectedYear,
    );
  }, [selectedYear, yearDetailQ.data, entries, currentBirthYear, currentProfileName, fiYear, retirementNoticesByYear]);

  if (planQ.isLoading || (planQ.isFetching && planQ.data == null)) return <LoadingSpinner />;
  if (planQ.error) {
    return (
      <ErrorState
        error={planQ.error}
        screen="fire-plan"
        metadata={{ failedQueries: "fire.getPlan" }}
        onRetry={onRefresh}
      />
    );
  }

  const thresholdLabel = plan?.threshold
    ? formatPercent(Number(plan.threshold), 0)
    : "80%";

  const primaryName = primaryProfile?.firstName ?? null;

  const spouseName = spouseProfile?.firstName ?? null;

  const summaryTitle = (lifecycleQ.data?.enabled && lifecycleTitle(lifecycleQ.data)) || (plan
    ? formatFiAgeSummary({
        fiYear,
        primaryName,
        primaryFiAge: fiAge,
        spouseName,
        spouseFiAge,
      })
    : "FIRE Plan");

  const summarySubtitle = (() => {
    if (!plan) return "Run the calculation to see your path to FI";
    if (lifecycleQ.data?.enabled && lifecycleQ.data.assessment && lifecycleTitle(lifecycleQ.data)) {
      return `${thresholdLabel} success target · Projection through ${lifecycleQ.data.assessment.endYear - 1}`;
    }
    if (!fiYear) return null; // handled separately below
    const parts: string[] = [];
    if (fiAge) parts.push(`Year ${fiYear}`);
    parts.push(`${thresholdLabel} success threshold`);
    if (anyFixed && targetYear) {
      const targetProfiles = profiles.filter((p) => p.targetFireYear != null);
      const targetParts = targetProfiles.map((p) => {
        const age = p.birthYear ? targetYear - p.birthYear : null;
        const name = p.firstName ?? (p.type === "primary" ? "Primary" : "Spouse");
        return age
          ? `${name} target retirement at age ${age}`
          : `${name} target retirement in ${targetYear}`;
      });
      parts.push(...targetParts);
    }
    return parts.join(" · ");
  })();

  return (
    <>
      <WebHead>
        <title>FIRE Plan – Per Diem</title>
      </WebHead>
      <ScrollView
        testID="fire-plan-screen"
        accessibilityLabel="FIRE plan screen"
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={c.primary}
          />
        }
      >
        <PageContainer className={isDesktop ? "" : "p-4"}>
          <PageHeader
            title="FIRE Plan"
            subtitle="Financial independence projections"
            action={
              <Pressable
                onPress={() => router.push('/(app)/tax-dashboard')}
                hitSlop={8}
                className="flex-row items-center gap-1"
              >
                <Ionicons name="receipt-outline" size={18} color={c.textSecondary} />
                <Text className="text-xs text-gray-600 dark:text-gray-400">
                  Tax Dashboard
                </Text>
              </Pressable>
            }
          />

          {!isDemo && (
            <View className="mb-4">
              <PlanHealthBanner />
            </View>
          )}

          <FireLifecycleCard />

          {/* FI Plan Summary */}
          <Card>
            {/* Title row */}
            <View className="flex-row items-start justify-between">
              <View className="flex-1 mr-3">
                <Text className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  {summaryTitle}
                </Text>
                {plan && !fiYear && hasSparseInputs && !(lifecycleQ.data?.enabled && lifecycleTitle(lifecycleQ.data)) ? (
                  <Text className="text-sm text-amber-600 dark:text-amber-400 mt-0.5">
                    Add {missingInputsCopy} to generate a meaningful projection.
                  </Text>
                ) : plan && !fiYear && !lifecycleQ.data?.persons.some(p => p.actualRetirementYear != null) ? (
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Your plan does not reach the{" "}
                    {plan.threshold ? formatPercent(Number(plan.threshold), 0) : "80%"}{" "}
                    success threshold yet.
                  </Text>
                ) : (
                  <Text className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {summarySubtitle}
                  </Text>
                )}
              </View>

              {/* Recalculate / Calculate — icon button */}
              {!isDemo && !calculationActive && (
                <Pressable onPress={handleCalculate} className="p-2 -mr-2">
                  <Ionicons
                    name="refresh"
                    size={20}
                    color={c.primary}
                  />
                </Pressable>
              )}
            </View>

            {/* Calculation progress (inline, replaces button while active) */}
            {calculationActive && (
              <View className="mt-3 gap-1.5">
                <View className="flex-row items-center gap-2">
                  <ActivityIndicator size="small" color={c.primary} />
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    {displayedCalcStatus}
                  </Text>
                  <Text className="text-sm text-gray-400 ml-auto">
                    {Math.round(displayedCalcProgress * 100)}%
                  </Text>
                </View>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  {calcMessages[calcMessageIndex]}
                </Text>
                <View className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                  <View
                    className="bg-green-500 h-1.5 rounded-full"
                    style={{
                      width: `${Math.round(displayedCalcProgress * 100)}%`,
                    }}
                  />
                </View>
              </View>
            )}

            {/* Last recalc impact (inline summary) */}
            {lastImpact && !calculationActive && (
              <View className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <Text className="text-sm text-gray-600 dark:text-gray-300">
                  {lifecycleQ.data?.persons.some(p => p.actualRetirementYear != null) ? 'Your plan was recalculated. Review the latest success rate above.' : buildLastImpactSentence(lastImpact, birthYear)}{" "}
                  <Text
                    onPress={() => setPanel("history")}
                    className="font-semibold underline text-forest-700 dark:text-green-500"
                  >
                    View history
                  </Text>
                </Text>
              </View>
            )}

            {/* Quick actions */}
            {plan && !calculationActive && (
              <View className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <View className="self-start">
                  <Pressable
                    onPress={handleOpenScenarios}
                    className="self-start flex-row items-center gap-2 rounded-xl bg-forest-600 px-4 py-2 active:bg-forest-700 dark:bg-forest-600"
                    accessibilityRole="button"
                    accessibilityLabel="Open FIRE scenarios"
                    onHoverIn={() => {
                      if (Platform.OS === "web") setIsScenariosCtaHovered(true);
                    }}
                    onHoverOut={() => {
                      if (Platform.OS === "web") setIsScenariosCtaHovered(false);
                    }}
                    style={Platform.OS === "web" ? ({ cursor: "pointer" } as object) : undefined}
                  >
                    <Ionicons name="git-branch-outline" size={16} color="#ffffff" />
                    <Text className="text-sm font-semibold text-white">
                      Scenarios
                    </Text>
                    {showScenariosFeatureHint && (
                      <View className="rounded-full bg-white px-2 py-0.5">
                        <Text className="text-[10px] font-bold uppercase text-forest-700">
                          New
                        </Text>
                      </View>
                    )}
                  </Pressable>

                  {Platform.OS === "web" &&
                    showScenariosFeatureHint &&
                    isScenariosCtaHovered && (
                      <View
                        className="absolute left-0 top-12 z-10 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                        style={{
                          width: 320,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.12,
                          shadowRadius: 18,
                        }}
                      >
                        <Text className="text-sm text-gray-700 dark:text-gray-200">
                          Save what-if FIRE plans for moves, work breaks, goals, or spending shifts.
                        </Text>
                      </View>
                    )}
                </View>
              </View>
            )}
          </Card>

          <View className="py-4 gap-4">
            {/* Panel tabs */}
            <PanelTabs active={panel} onChange={setPanel} />

            {/* Active panel */}
            {panel === "cashflow" && plan && (
              <CashFlowPanel
                entries={entries}
                plan={plan}
                birthYear={birthYear}
                onRowPress={setSelectedYear}
              />
            )}
            {panel === "events" && <EventsSection />}
            {panel === "montecarlo" && plan && (
              <MonteCarloPanel
                entries={entries}
                plan={plan}
                birthYear={birthYear}
              />
            )}
            {panel === "drawdown" && plan && (
              <DrawdownPanel entries={entries} />
            )}
            {panel === "history" && (
              <HistoryPanel
                history={recalcHistory}
                birthYear={birthYear}
                isLoading={recalcHistoryQ.isLoading}
              />
            )}

            {!plan && panel !== "events" && panel !== "history" && (
              <View>
                <Card className="border-2 border-dashed border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30 items-center py-8">
                  <Text className="text-base font-semibold text-green-800 dark:text-green-300">
                    No FIRE Plan Yet
                  </Text>
                  <Text className="text-sm text-green-600 dark:text-green-500 text-center mt-1 px-4">
                    Tap "Calculate" above to run your first Monte Carlo
                    simulation.
                  </Text>
                </Card>
              </View>
            )}
          </View>
        </PageContainer>
      </ScrollView>

      <CashFlowModal
        visible={selectedYear !== null}
        onClose={() => setSelectedYear(null)}
        data={yearDetailData}
        isLoading={yearDetailQ.isLoading}
        emptyMessage={yearDetailQ.error
          ? "Unable to load cash flow details. Close this window and try again."
          : "Recalculate your FIRE plan to view a breakdown that matches your saved projection."}
      />

      <WhatIfModal
        visible={showWhatIf}
        onClose={() => setShowWhatIf(false)}
        currentFiYear={fiYear}
        currentSuccessRate={currentSuccessRate}
        defaultMonthlyExpenses={defaultMonthlyExpenses}
        defaultMonthlyIncome={defaultMonthlyIncome}
        birthYear={birthYear}
      />
    </>
  );
}

function EventsSection() {
  const isFocused = useIsFocused();
  const c = useColors();
  const utils = trpc.useUtils();
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const planQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const eventsQ = trpc.fire.listEvents.useQuery(undefined, { enabled: isFocused });
  const suggestionsQ = trpc.fire.getSuggestedEvents.useQuery(undefined, { enabled: isFocused });
  const applyTemplateMutation = trpc.fire.applyEventTemplate.useMutation();
  const dismissSuggestionMutation = trpc.fire.dismissSuggestion.useMutation();
  const createEventMutation = trpc.fire.createEvent.useMutation();
  const updateEventMutation = trpc.fire.updateEvent.useMutation();
  const deleteEventMutation = trpc.fire.deleteEvent.useMutation();
  const currentYear = new Date().getFullYear();

  const [adding, setAdding] = useState(false);
  const isDemo = householdQ.data?.isDemo ?? false;

  const events = (eventsQ.data ?? []) as GoalEvent[];
  const profiles = (profilesQ.data ?? []) as PersonProfile[];
  const defaultPlanEndYear = planQ.data?.endYear ?? currentYear + 30;
  const currentFiYear = planQ.data?.beginYear ?? null;

  const invalidateEvents = async () => {
    await Promise.all([
      utils.fire.listEvents.invalidate(),
      utils.fire.getPlan.invalidate(),
      utils.fire.getLastRecalcImpact.invalidate(),
      utils.fire.getRecalcStatus.invalidate(),
    ]);
  };

  const handleAddEvent = async (values: LifeEventFormValues) => {
    await createEventMutation.mutateAsync(toCreateLifeEventMutationInput(values));
    await invalidateEvents();
    setAdding(false);
  };

  const handleUpdateEvent = async (id: string, values: LifeEventFormValues) => {
    await updateEventMutation.mutateAsync(toUpdateLifeEventMutationInput(id, values));
    await invalidateEvents();
  };

  const handleDeleteEvent = async (id: string) => {
    await deleteEventMutation.mutateAsync({ id });
    await invalidateEvents();
  };

  const unappliedSuggestions = (suggestionsQ.data ?? []).filter((s) => !s.isApplied);

  const handleApplySuggestion = async (templateGroupId: string) => {
    await applyTemplateMutation.mutateAsync({ templateGroupId });
    await invalidateEvents();
    suggestionsQ.refetch();
  };

  const handleDismissSuggestion = async (templateGroupId: string) => {
    await dismissSuggestionMutation.mutateAsync({ templateGroupId });
    suggestionsQ.refetch();
  };

  return (
    <View>
      {!isDemo && unappliedSuggestions.length > 0 && (
        <Card className="mb-4">
          <SectionHeader title="Suggested Events" />
          <Text className="text-xs text-gray-400 mb-3">
            Based on your family and retirement plans
          </Text>
          {unappliedSuggestions.map((s) => (
            <View
              key={s.templateGroupId}
              className="flex-row items-center gap-3 py-2 border-b border-gray-50 dark:border-gray-700 last:border-0"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {s.label}
                </Text>
                <Text className="text-xs text-gray-400">{s.description}</Text>
                <Text className="text-xs text-gray-400 mt-0.5">
                  {s.estimatedAnnualCost > 0
                    ? `~${formatCurrency(s.estimatedAnnualCost, { compact: true })}/yr`
                    : `${formatCurrency(s.estimatedOneTimeCost, { compact: true })} one-time`}
                </Text>
              </View>
              <View className="flex-row gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() => handleApplySuggestion(s.templateGroupId)}
                  loading={applyTemplateMutation.isPending}
                >
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onPress={() => handleDismissSuggestion(s.templateGroupId)}
                  loading={dismissSuggestionMutation.isPending}
                >
                  Dismiss
                </Button>
              </View>
            </View>
          ))}
        </Card>
      )}

      <Card>
        <SectionHeader
          title="Life Events"
          action={
            !adding && !isDemo ? (
              <Pressable
                onPress={() => setAdding(true)}
                className="p-1"
              >
                <Ionicons name="add" size={20} color={c.primary} />
              </Pressable>
            ) : undefined
          }
        />

        {adding && !isDemo && (
          <View className="mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
            <LifeEventForm
              profiles={profiles}
              currentFireYear={currentFiYear}
              defaultPlanEndYear={defaultPlanEndYear}
              onSave={handleAddEvent}
              onCancel={() => setAdding(false)}
              saving={createEventMutation.isPending}
            />
          </View>
        )}

        <LifeEventList
          events={events}
          profiles={profiles}
          currentFireYear={currentFiYear}
          defaultPlanEndYear={defaultPlanEndYear}
          onUpdate={handleUpdateEvent}
          onDelete={handleDeleteEvent}
          isDemo={isDemo}
          isLoading={eventsQ.isLoading}
        />
      </Card>
    </View>
  );
}
